import { useState, useRef, useEffect } from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { BsCalendar2DateFill } from "react-icons/bs";
import { IoClose } from "react-icons/io5";

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DateRangePicker = ({ onRangeChange }) => {
  const today = new Date();
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [appliedRange, setAppliedRange] = useState(null); // 👈 stores confirmed range
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();
  const buildDate = (day) => new Date(viewYear, viewMonth, day);

  const formatDisplay = (date) => {
    if (!date) return null;
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const formatValue = (date) => {
    if (!date) return "";
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${y}-${m}-${d}`;
  };

  const handleDayClick = (day) => {
    const clicked = buildDate(day);
    if (!startDate || (startDate && endDate)) {
      setStartDate(clicked);
      setEndDate(null);
    } else {
      const finalEnd = clicked < startDate ? startDate : clicked;
      const finalStart = clicked < startDate ? clicked : startDate;
      setStartDate(finalStart);
      setEndDate(finalEnd);
    }
  };

  const handleApply = () => {
    if (!startDate || !endDate) return;
    const range = {
      start: formatValue(startDate),
      end: formatValue(endDate),
      displayStart: formatDisplay(startDate),
      displayEnd: formatDisplay(endDate),
    };
    setAppliedRange(range);
    onRangeChange?.({ start: range.start, end: range.end });
    setIsOpen(false);
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedRange(null);
    onRangeChange?.({ start: "", end: "" });
  };

  const isStart = (day) =>
    startDate && buildDate(day).toDateString() === startDate.toDateString();
  const isEnd = (day) =>
    endDate && buildDate(day).toDateString() === endDate.toDateString();

  const isInRange = (day) => {
    const d = buildDate(day);
    const rangeEnd = endDate || hoverDate;
    if (!startDate || !rangeEnd) return false;
    const [s, e] =
      startDate < rangeEnd ? [startDate, rangeEnd] : [rangeEnd, startDate];
    return d > s && d < e;
  };

  const isToday = (day) =>
    buildDate(day).toDateString() === today.toDateString();

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const totalDays = getDaysInMonth(viewMonth, viewYear);
  const firstDay = getFirstDayOfMonth(viewMonth, viewYear);

  const triggerText = appliedRange
    ? `${appliedRange.displayStart}  →  ${appliedRange.displayEnd}`
    : startDate && !endDate
      ? `${formatDisplay(startDate)}  →  Select end date`
      : "Select date range";

  return (
    <div className="flex flex-col gap-2">
      <div className="relative w-fit" ref={ref}>
        {/* Trigger */}
        <div
          onClick={() => setIsOpen((p) => !p)}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg border bg-gray-50 cursor-pointer transition-all duration-300 min-w-[280px]
            ${isOpen ? "border-gray-800 bg-white shadow-[0_0_0_3px_rgba(17,24,39,0.08)]" : "border-gray-200 hover:border-gray-500 hover:bg-white hover:shadow-md"}
            ${appliedRange ? "border-gray-800 bg-white" : ""}`}
        >
          <BsCalendar2DateFill
            className={`text-base shrink-0 transition-colors duration-300 ${appliedRange || isOpen ? "text-gray-800" : "text-gray-400"}`}
          />
          <span
            className={`text-sm font-medium tracking-wide flex-1 ${appliedRange || startDate ? "text-gray-800" : "text-gray-400"}`}
          >
            {triggerText}
          </span>
          {appliedRange && (
            <IoClose
              className="text-gray-400 hover:text-gray-800 transition-colors duration-200 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          )}
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute top-[110%] left-0 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-72">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={handlePrevMonth}
                className="p-1 rounded-md hover:bg-gray-100 transition"
              >
                <IoIosArrowBack className="text-gray-600 text-lg" />
              </button>
              <span className="text-sm font-semibold text-gray-800">
                {MONTHS[viewMonth]} {viewYear}
              </span>
              <button
                onClick={handleNextMonth}
                className="p-1 rounded-md hover:bg-gray-100 transition"
              >
                <IoIosArrowForward className="text-gray-600 text-lg" />
              </button>
            </div>

            {/* Instruction */}
            <p className="text-[10px] text-center text-gray-400 mb-2 uppercase tracking-wide font-medium">
              {!startDate
                ? "Select start date"
                : !endDate
                  ? "Select end date"
                  : ""}
            </p>

            {/* Day Labels */}
            <div className="grid grid-cols-7 mb-1">
              {DAYS.map((d) => (
                <div
                  key={d}
                  className="text-center text-[10px] font-semibold text-gray-400 uppercase"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7">
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`e-${i}`} />
              ))}
              {Array.from({ length: totalDays }).map((_, i) => {
                const day = i + 1;
                const start = isStart(day);
                const end = isEnd(day);
                const inRange = isInRange(day);
                const todayDay = isToday(day);

                return (
                  <div
                    key={day}
                    onClick={() => handleDayClick(day)}
                    onMouseEnter={() =>
                      startDate && !endDate && setHoverDate(buildDate(day))
                    }
                    onMouseLeave={() => setHoverDate(null)}
                    className={`relative text-xs py-[6px] text-center cursor-pointer font-medium transition-all duration-100 select-none
                      ${start || end ? "bg-gray-800 text-white rounded-lg z-10" : ""}
                      ${inRange ? "bg-gray-100 text-gray-700" : ""}
                      ${start && endDate ? "rounded-lg" : ""}
                      ${end ? "rounded-lg" : ""}
                      ${!start && !end && !inRange ? "hover:bg-gray-100 rounded-lg text-gray-700" : ""}
                      ${todayDay && !start && !end ? "border border-gray-800 rounded-lg" : ""}
                    `}
                  >
                    {day}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
              <button
                onClick={handleClear}
                className="text-xs font-semibold text-red-400 hover:text-red-600 transition"
              >
                Clear
              </button>
              <button
                onClick={handleApply}
                disabled={!startDate || !endDate}
                className={`text-xs font-semibold px-4 py-[6px] rounded-lg transition-all duration-200
                  ${
                    startDate && endDate
                      ? "bg-gray-800 text-white hover:bg-gray-700 shadow-sm"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Applied Range Badge */}
      {appliedRange && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">
            Filtered:
          </span>
          <div className="flex items-center gap-1 bg-gray-800 text-white text-[11px] font-semibold px-3 py-1 rounded-full">
            <span>{appliedRange.displayStart}</span>
            <span className="opacity-50 mx-1">→</span>
            <span>{appliedRange.displayEnd}</span>
            <IoClose
              className="ml-1 opacity-60 hover:opacity-100 cursor-pointer transition-opacity"
              onClick={handleClear}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
